/**
 * CompanySettings Component
 * 
 * Company-specific settings (default currency, fiscal year, etc)
 */

// External libraries
import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form@7.55.0';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

// Internal - Components
import { 
  Card, 
  CardHeader, 
  CardTitle, 
  CardDescription, 
  CardContent 
} from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { CurrencySelect } from '../../../components/financial';

// Internal - Types
import type { Company } from '../../../types/database';
import type { Currency } from '../../../types/currency';

// Internal - Constants
import { CURRENCY_CODES } from '../../../lib/constants/currencies';

const companySettingsSchema = z.object({
  default_currency: z.enum(CURRENCY_CODES as [string, ...string[]], {
    errorMap: () => ({ message: 'Moeda inválida' }),
  }),
});

type CompanySettingsFormData = z.infer<typeof companySettingsSchema>;

interface CompanySettingsProps {
  company: Company;
  onUpdateCompany: (data: Partial<Company>) => Promise<void>;
  isUpdating?: boolean;
}

export function CompanySettings({ 
  company, 
  onUpdateCompany, 
  isUpdating 
}: CompanySettingsProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { isDirty },
  } = useForm<CompanySettingsFormData>({
    resolver: zodResolver(companySettingsSchema),
    defaultValues: {
      default_currency: company.default_currency,
    },
  });

  // Reset form when company data changes (e.g., after refetch)
  useEffect(() => {
    console.log('[CompanySettings] Company data changed, resetting form with:', company.default_currency);
    reset({
      default_currency: company.default_currency,
    });
  }, [company.default_currency, reset]);

  const onSubmit = async (data: CompanySettingsFormData) => {
    console.log('[CompanySettings] Submitting update:', {
      companyId: company.id,
      companyName: company.name,
      currentCurrency: company.default_currency,
      newCurrency: data.default_currency
    });
    
    try {
      await onUpdateCompany({ default_currency: data.default_currency });
      console.log('[CompanySettings] Update successful');
      toast.success('Configurações da empresa atualizadas!');
      // Reset form with new values to mark as not dirty
      reset(data);
    } catch (error) {
      console.error('[CompanySettings] Error updating company settings:', error);
      toast.error('Erro ao atualizar configurações');
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Building2 className="w-5 h-5" />
          Configurações da Empresa
        </CardTitle>
        <CardDescription>
          Configurações específicas da empresa {company.name}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Default Currency */}
          <div className="space-y-2">
            <Label htmlFor="default_currency">
              Moeda Padrão
            </Label>
            <p className="text-sm text-muted-foreground mb-2">
              Moeda padrão para novos lançamentos e relatórios desta empresa
            </p>
            <Controller
              name="default_currency"
              control={control}
              render={({ field }) => (
                <CurrencySelect
                  value={field.value}
                  onChange={field.onChange}
                  className="w-full md:w-64"
                />
              )}
            />
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button 
              type="submit" 
              disabled={!isDirty || isUpdating}
              className="gap-2"
            >
              {isUpdating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                'Salvar Alterações'
              )}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}
